/*
 *
 *  * Copyright(c) 2021 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */

import { Component, OnInit } from '@angular/core';
import { AppConfigService } from '@app/common/app-config.service';
import { AuditService } from '@app/common/audit.service';
import { EventMessage, Events, EventService } from '@app/common/event';
import { NotifyService } from '@app/common/notify.service';
import { EventWorkItem } from '@app/data-model/event-work-item';
import { Logger, MessageService } from '@core/lib/services';
import { TranslateService } from '@ngx-translate/core';
import { ModalConfig, ModalRef } from '@wfm/core';
import { NativeAcademicWorkflowService } from '@app/di/native-academic-workflow.service';

const log = new Logger('AssignToMeConfirmationComponent');

@Component({
  selector: 'app-assign-to-me-confirmation',
  templateUrl: './assign-to-me-confirmation.component.html',
  styleUrls: ['./assign-to-me-confirmation.component.scss']
})
export class AssignToMeConfirmationComponent implements OnInit {
  showNawWarning: boolean;

  constructor(
    public config: ModalConfig,
    public modalRef: ModalRef,
    private auditService: AuditService,
    private eventService: EventService,
    private appConfigService: AppConfigService,
    private translate: TranslateService,
    private notify: NotifyService,
    private messageService: MessageService,
    private nativeAcademicWorkflowService: NativeAcademicWorkflowService
  ) {}

  ngOnInit(): void {
    this.showNawWarning = this.nativeAcademicWorkflowService.containsNawExam(this.config.data.item);
  }

  assignWorkItem() {
    if (this.config.data.item.state && this.config.data.item.state.isActivatedByOther) {
      this.messageService.broadcast('previewExam', this.config.data.item);
    } else {
      if (this.config.data.item.state && this.config.data.item.state.isAssigned) {
        this.eventService
          .sendEvent(
            new EventMessage(Events.unassign, new EventWorkItem(this.config.data.item.id), {
              userName: this.config.data.item.state.ownership.assignedToUser[0].userName
            })
          )
          .then(
            () => {
              log.info('unassign action executed successfully');
            },
            () => {
              log.info('error during unassign action');
            }
          )
          .finally(() => {
            this.modalRef.close(false);
            this.assign();
          });
      } else {
        this.modalRef.close(false);
        this.assign();
      }
    }
  }

  assignAndLaunchViewer() {
    if (this.config.data.item.state && this.config.data.item.state.isActivatedByOther) {
      this.previewWorkItem();
    } else {
      if (this.config.data.item.state && this.config.data.item.state.isAssigned) {
        this.eventService
          .sendEvent(
            new EventMessage(Events.unassign, new EventWorkItem(this.config.data.item.id), {
              userName: this.config.data.item.state.ownership.assignedToUser[0].userName
            })
          )
          .then(
            () => {
              log.info('unassign action executed successfully');
            },
            () => {
              log.info('error during unassign action');
            }
          )
          .finally(() => {
            this.launchExam(true);
          });
      } else {
        this.launchExam(true);
      }
    }
  }

  private launchExam(result?) {
    this.modalRef.close(result);
    this.messageService.broadcast('launchExam', {
      item: this.config.data.item,
      launchedFromAssignToMeDialog: true
    });
  }

  previewWorkItem() {
    this.messageService.broadcast('previewExam', this.config.data.item);
    this.modalRef.close(true);
  }

  assign() {
    let auditData = {} as any;
    const data = { id: this.config.data.item.id };
    if (this.config.data.item) {
      auditData = this.auditService.getExamAuditInfo(this.config.data.item);
    }

    if (!this.config.data.item.state.isAssigned) {
      const actor = this.appConfigService.getUserId();
      auditData.ownerName = actor;

      this.eventService
        .sendEvent(
          new EventMessage(Events.assign, new EventWorkItem(this.config.data.item.id, data), {
            userName: actor,
            examAudit: auditData
          })
        )
        .then(
          () => {
            log.info(`assign exam success, ${this.config.data.item.id}`);
          },
          () => {
            this.notify.error(this.translate.instant('workItem.assignText'));
          }
        );
    }
  }
}
